import _ from 'lodash'
import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Motion, spring } from 'react-motion'

import { Classes } from '@blueprintjs/core'

import { Button } from 'browser/components/atomic-elements/atoms/button/button'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_ui2-signature-pad.scss'

const LINE_WIDTH = 4
const STROKE_STYLE = 'black'

export interface IUI2SignaturePadProps {
  /** Heading shown in the pad's top toolbar. */
  title?: string
  /**
   * Suppresses the portrait rotation trick. Desktop web passes true — the
   * viewport is already wide, so the canvas is used as-is. Mobile web leaves
   * it false so a portrait phone gets a landscape-width signing surface.
   */
  portraitOnly?: boolean
  /** Receives the flattened PNG of the drawn signature. Not called if nothing was drawn. */
  onDone: (blob: Blob) => void
  onClose: () => void
}

type Point = { x: number; y: number }

interface IDrawingInfo {
  lastPoint?: Point
  hasData?: boolean
  isLandscape?: boolean
}

/**
 * Freehand signature canvas shared by DW and MW. Rendered inside whatever
 * overlay the caller opens; it owns only the drawing surface and hands back a
 * PNG blob, leaving entity/multipart persistence to the field.
 */
export const UI2SignaturePad: React.FC<IUI2SignaturePadProps> = (props) => {
  const { title = 'Signature', portraitOnly = false, onDone, onClose } = props

  const infoRef = useRef<IDrawingInfo>({})
  const contextRef = useRef<CanvasRenderingContext2D>()
  const bottomToolbarRef = useRef<HTMLDivElement>()
  const topToolbarRef = useRef<HTMLDivElement>()
  const containerRef = useRef<HTMLDivElement>()
  const canvasRef = useRef<HTMLCanvasElement>()
  const rotatedRef = useRef<HTMLDivElement>()
  const rootRef = useRef<HTMLDivElement>()

  const [animation, setAnimation] = useState({
    from: { opacity: 0 },
    to: { opacity: 1 } as any,
  })

  const handleInteraction = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    draw(canvasRef.current, contextRef.current, event, infoRef.current, setAnimation)
  }, [])

  const handleClear = useCallback(() => {
    setAnimation({ from: { opacity: 0 }, to: { opacity: spring(1) } })
    infoRef.current.hasData = false
    setCanvasStyle(contextRef.current)
    fillCanvas(canvasRef.current, contextRef.current)
  }, [])

  const handleDone = useCallback(async () => {
    if (!infoRef.current.hasData) {
      onClose()
      return
    }
    const blob = await toBlob(canvasRef.current)
    if (!_.isNil(blob)) {
      onDone(blob)
    }
    onClose()
  }, [onDone, onClose])

  const handleResize = useCallback(
    _.debounce(() => {
      if (!canvasRef.current) return

      // Size the rotated div against its parent's opposite dimension so the
      // rotated surface still fills the viewport.
      const parent = rootRef.current
      const landscape = portraitOnly || isLandscape()
      const width = landscape ? parent.clientHeight : parent.clientWidth
      const height = landscape ? parent.clientWidth : parent.clientHeight
      rotatedRef.current.style.width = height + 'px'
      rotatedRef.current.style.height = width + 'px'

      // Stash pixels offscreen: resizing a canvas clears it, and a resize can
      // fire when the user lifts their finger on the very first stroke.
      const offscreen = document.createElement('canvas')
      offscreen.width = canvasRef.current.width
      offscreen.height = canvasRef.current.height
      offscreen.getContext('2d').drawImage(canvasRef.current, 0, 0)

      resizeCanvas(
        canvasRef.current,
        rotatedRef.current,
        containerRef.current,
        topToolbarRef.current,
        bottomToolbarRef.current,
      )
      setCanvasStyle(contextRef.current)
      fillCanvas(canvasRef.current, contextRef.current)
      contextRef.current.drawImage(offscreen, 0, 0)
    }, 100),
    [portraitOnly],
  )

  useEffect(() => {
    contextRef.current = canvasRef.current.getContext('2d')
    handleResize()
    infoRef.current.isLandscape = portraitOnly || isLandscape()

    const observer = new ResizeObserver(handleResize)
    observer.observe(containerRef.current)
    window.addEventListener('resize', handleResize)

    const portraitQuery = window.matchMedia('(orientation: portrait)')
    const portraitListener = (event: MediaQueryListEvent) => {
      infoRef.current.isLandscape = portraitOnly || !event.matches
      handleResize()
    }
    portraitQuery.addEventListener('change', portraitListener)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', handleResize)
      portraitQuery.removeEventListener('change', portraitListener)
    }
  }, [])

  return (
    <div ref={rootRef} className='ui2-signaturePad'>
      <div
        ref={rotatedRef}
        className={classNames('ui2-signaturePad-surface', {
          'ui2-signaturePad-surface--rotatable': !portraitOnly,
        })}
      >
        <div ref={topToolbarRef} className='ui2-signaturePad-toolbar'>
          <Button className='ui2-signaturePad-closeButton' onClick={onClose} aria-label='Close'>
            <UI2Icon name={UI2IconNames.X} />
          </Button>
          <span className='ui2-signaturePad-title'>{title}</span>
        </div>
        <div ref={containerRef} className='ui2-signaturePad-canvasFrame'>
          <div className='ui2-signaturePad-canvasClip'>
            <canvas
              ref={canvasRef}
              className='ui2-signaturePad-canvas'
              onTouchStart={handleInteraction}
              onTouchEnd={handleInteraction}
              onTouchMove={handleInteraction}
              onMouseDown={handleInteraction}
              onMouseUp={handleInteraction}
              onMouseMove={handleInteraction}
              onContextMenu={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            />
            <Motion defaultStyle={animation.from} style={animation.to}>
              {(style) => (
                <div className='ui2-signaturePad-hint' style={style}>
                  Please sign here
                </div>
              )}
            </Motion>
          </div>
        </div>
        <div ref={bottomToolbarRef} className='ui2-signaturePad-actions'>
          <Button className='ui2-signaturePad-actionButton' onClick={handleClear}>
            Clear Signature
          </Button>
          <Button
            className={classNames('ui2-signaturePad-actionButton', Classes.INTENT_PRIMARY)}
            onClick={handleDone}
          >
            Done Signing
          </Button>
        </div>
      </div>
    </div>
  )
}

function draw(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  event: React.MouseEvent | React.TouchEvent,
  drawingInfo: IDrawingInfo,
  setAnimation: (animation: any) => void,
) {
  event.preventDefault()
  event.stopPropagation()

  const eventData = (event as React.TouchEvent)?.touches?.[0] ?? (event as React.MouseEvent)

  if (event.type === 'touchstart' || event.type === 'mousedown') {
    drawingInfo.lastPoint = getPoint(canvas, eventData, drawingInfo)
  } else if (event.type === 'touchend' || event.type === 'mouseup') {
    drawingInfo.lastPoint = null
  } else if (drawingInfo.lastPoint) {
    const point = getPoint(canvas, eventData, drawingInfo)
    context.beginPath()
    context.moveTo(drawingInfo.lastPoint.x, drawingInfo.lastPoint.y)
    context.lineTo(point.x, point.y)
    context.stroke()
    if (!drawingInfo.hasData) {
      setAnimation({ from: { opacity: 1 }, to: { opacity: spring(0) } })
    }
    drawingInfo.hasData = true
    drawingInfo.lastPoint = point
  }
}

function fillCanvas(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) {
  context.fillRect(0, 0, canvas.width, canvas.height)
}

function getPoint(
  canvas: HTMLCanvasElement,
  eventData: React.Touch | React.MouseEvent,
  drawingInfo: IDrawingInfo,
): Point {
  const bounds = canvas.getBoundingClientRect()
  const ex = eventData.pageX - bounds.left - scrollX
  const ey = eventData.pageY - bounds.top - scrollY
  if (drawingInfo.isLandscape) {
    return { x: ex, y: ey }
  }
  // Portrait is rotated in CSS, so transform screen coords into canvas space.
  return { x: ey, y: canvas.height - ex }
}

function resizeCanvas(
  canvas: HTMLCanvasElement,
  outerContainer: HTMLDivElement,
  innerContainer: HTMLDivElement,
  topToolbar: HTMLDivElement,
  bottomToolbar: HTMLDivElement,
) {
  const outerStyle = window.getComputedStyle(outerContainer)
  const innerStyle = window.getComputedStyle(innerContainer)
  const verticalOffset =
    topToolbar.offsetHeight +
    bottomToolbar.offsetHeight +
    parseFloat(outerStyle.paddingTop) +
    parseFloat(outerStyle.paddingBottom) +
    parseFloat(innerStyle.paddingTop) +
    parseFloat(innerStyle.paddingBottom) +
    parseFloat(innerStyle.marginTop) +
    parseFloat(innerStyle.marginBottom)

  canvas.width = outerContainer.offsetWidth
  canvas.height = outerContainer.offsetHeight - verticalOffset
}

function setCanvasStyle(context: CanvasRenderingContext2D) {
  context.strokeStyle = STROKE_STYLE
  context.fillStyle = 'white'
  context.lineWidth = LINE_WIDTH
  context.lineJoin = 'round'
  context.lineCap = 'round'
}

function isLandscape(): boolean {
  const orientation =
    screen.orientation?.type ||
    screen['mozOrientation'] ||
    screen['msOrientation'] ||
    (window.innerHeight < window.innerWidth ? 'landscape-primary' : 'portrait-primary')
  return orientation === 'landscape-primary' || orientation === 'landscape-secondary'
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob))
  })
}
